import React, { useEffect, useState } from 'react';
import { 
  Box, Typography, Paper, IconButton, Tooltip, Chip, 
  Button, Breadcrumbs, Link, CircularProgress,
  ThemeProvider, createTheme, FormControl, InputLabel, Select, MenuItem,
  TextField, Grid, List, ListItem, ListItemText, ListItemSecondaryAction,
  Divider, Pagination
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useNotify } from '../../../components/Datatable/NotifyProvider';
import { useAuth } from '../../../components/AuthProvider';
import { useLoading } from '../../../components/LoadingProvider';
import api from '../../../services/api';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FolderIcon from '@mui/icons-material/Folder';
import Swal from 'sweetalert2';

// Components
import CategoryForm from './CategoryForm';
const moment = require('moment');

export default function CategoryList() {
  const navigate = useNavigate();
  const notify = useNotify();
  const auth = useAuth();
  const loading = useLoading();
  const { isLoading, setLoading } = loading;
  
  // Estados para os filtros e paginação
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Estado para os dados
  const [categories, setCategories] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [categoryFormState, setCategoryFormState] = useState({
    open: false,
    id: null,
    category: null
  });
  
  // Opções do filtro de status
  const statusOptions = [
    { value: 'all', label: 'Todos' },
    { value: 'active', label: 'Ativos' },
    { value: 'inactive', label: 'Inativos' }
  ];

  // Opções de itens por página
  const pageSizeOptions = [
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' }
  ];
  
  // Carregar categorias com base nos filtros e paginação
  const fetchCategories = async () => {
    try {
      setLoading(true);
      
      // Construir a URL com os parâmetros de query
      let url = '/dns/blacklist/categories/all?';
      
      // Adicionar paginação
      url += `page=${page}&limit=${pageSize}`;
      
      // Adicionar filtro de status se não for 'all'
      if (statusFilter !== 'all') {
        const isActive = statusFilter === 'active' ? 1 : 0;
        url += `&active=${isActive}`;
      }
      
      // Adicionar filtro de busca se existir
      if (searchFilter.trim() !== '') {
        url += `&search=${encodeURIComponent(searchFilter.trim())}`;
      }
      
      const response = await api.get(url);
      const total = response.data.total
      if (response.data && !response.data.error) {
        setCategories(response.data.data || []);
        setTotalPages(Math.ceil(total / pageSize) || 1);
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao carregar categorias',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados quando os filtros ou paginação mudarem
  useEffect(() => {
    fetchCategories();
  }, [page, pageSize, statusFilter, refresh]);

  // Função para aplicar o filtro de busca
  const handleSearch = () => {
    setPage(1); // Resetar para a primeira página ao buscar
    fetchCategories();
  };

  // Função para limpar todos os filtros
  const clearFilters = () => {
    setStatusFilter('all');
    setSearchFilter('');
    setPage(1);
    setRefresh(!refresh);
  };

  // Função para abrir o formulário de adicionar categoria
  const handleAddCategory = () => {
    setCategoryFormState({
      open: true,
      id: null,
      category: null
    });
  };

  // Função para editar categoria
  const handleEditCategory = (category) => {
    setCategoryFormState({
      open: true,
      id: category.id,
      category: category
    });
  };

  // Função para ver detalhes da categoria
  const handleViewCategory = (categoryId) => {
    navigate(`/dns_blacklist/category/${categoryId}`);
  };

  // Função para excluir categoria
  const handleDeleteCategory = async (category) => {
    try {
      setLoading(true);
      const response = await api.delete(`/dns/blacklist/categories/${category.id}`);
      
      if (response.data.success) {
        notify.setNotify({
          open: true,
          message: response.data.success,
          severity: 'success'
        });
        
        // Atualizar dados
        setRefresh(!refresh);
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao excluir categoria',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para salvar a categoria (criar/atualizar)
  const handleSubmitCategory = async (categoryData) => {
    try {
      setLoading(true);
      
      let response;
      if (categoryFormState.id) {
        // Atualizar categoria existente
        response = await api.put(`/dns/blacklist/categories/${categoryFormState.id}`, categoryData);
        
        if (response.data.id) {
          notify.setNotify({
            open: true,
            message: 'Categoria atualizada com sucesso',
            severity: 'success'
          });
        }
      } else {
        // Criar nova categoria
        response = await api.post('/dns/blacklist/categories/create', categoryData);
        
        if (response.data.id) {
          notify.setNotify({
            open: true,
            message: 'Categoria criada com sucesso',
            severity: 'success'
          });
        }
      }
      
      if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      } else {
        // Fechar o formulário
        setCategoryFormState({ open: false, id: null, category: null });
        
        // Atualizar dados
        setRefresh(!refresh);
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao salvar categoria',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Atualizar o título da página
  React.useEffect(() => {
    document.title = 'IPV7 Tools - DNS Blacklist';
  }, []);

  return (
    <ThemeProvider theme={createTheme({
      typography:{
        fontFamily:[
          'sans-serif',
          'Roboto',
          'Montserrat',
        ]
      },
      palette: {
        mode: 'light',
        primary: {
          main: "#e01b29",
        },
      }
    })}>
      {/* Formulário de categoria */}
      <CategoryForm
        open={categoryFormState.open}
        category={categoryFormState.category}
        onClose={() => setCategoryFormState({ open: false, id: null, category: null })}
        onSubmit={handleSubmitCategory}
      />
      
      {/* Cabeçalho */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Typography color="text.primary">
            DNS Blacklist
          </Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h1">
            Categorias
          </Typography>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!auth.hasPerm("dns_blacklist.create")}
            onClick={handleAddCategory}
          >
            Nova Categoria
          </Button>
        </Box>
      </Box>
      
      {/* Filtros */}
      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1); // Resetar para a primeira página ao mudar o filtro
                }}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={7}>
            <TextField
              fullWidth
              size="small"
              label="Buscar por nome"
              variant="outlined"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
                }}
                InputProps={{
                endAdornment: (
                  <IconButton
                  size="small"
                  onClick={handleSearch}
                  >
                  <SearchIcon />
                  </IconButton>
                ),
                }}
                />
                </Grid>
                
                <Grid item xs={12} sm={2} md={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Tooltip title="Limpar filtros">
                <IconButton onClick={clearFilters}>
                <RemoveCircleOutline />
                </IconButton>
                </Tooltip>
                
                <Tooltip title="Atualizar">
                <IconButton onClick={() => setRefresh(!refresh)}>
                <RefreshIcon />
                </IconButton>
                </Tooltip>
                </Grid>
              </Grid>
              </Paper>
              
              
              <Paper elevation={3} sx={{ p: 2 }}>
                {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
                </Box>
                ) : categories.length > 0 ? (
                <>
                <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {categories.map((category, index) => {
                // Definir cor da pasta: cinza se expirado, senão primary
                let folderColor = "primary";
                if (category?.expired) {
                  const expDate = moment(category.expired);
                  const now = moment();
                  if (expDate.isBefore(now, 'day')) {
                  folderColor = "disabled";
                  }
                }
                return (
                <React.Fragment key={category.id}>
                <ListItem
                alignItems="flex-start"
                button
                onClick={() => handleViewCategory(category.id)}
                sx={{ cursor: 'pointer' }}
                >
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  <FolderIcon color={folderColor} />
                </Box>
                <ListItemText
                primary={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'medium' }}>
                  {category.name}
                </Typography>
                <Chip 
                  label={category.active ? 'Ativo' : 'Inativo'} 
                  size="small"
                  color={category.active ? 'success' : 'default'}
                  sx={{ ml: 2 }}
                />
                <Chip 
                  label={`${category.domains_count || 0} domínios`} 
                  size="small"
                  sx={{ ml: 1 }}
                />
                {/* Mostra data de expiração e dias restantes */}
                {category?.expired && (() => {
                  const expDate = moment(category.expired);
                  const now = moment();
                  if (expDate.isBefore(now, 'day')) {
                  return (
                  <Chip 
                  label={`expirado em ${expDate.format('DD/MM/YYYY')}`}
                  size="small"
                  sx={{ ml: 1 }}
                  color="error"
                  />
                  );
                  } else {
                  const daysLeft = expDate.diff(now, 'days');
                  return (
                  <Chip 
                  label={`expira dia ${expDate.format('DD/MM/YYYY')} (${daysLeft} dias)`}
                  size="small"
                  sx={{ ml: 1 }}
                  color={daysLeft <= 3 ? 'warning' : 'info'}
                  />
                  );
                  }
                })()}
                </Box>
                }
                secondary={
                <Typography
                sx={{ display: 'inline' }}
                component="span"
                variant="body2"
                color="text.primary"
                >
                {category.description || '-'}
                </Typography>
                }
                />
                <ListItemSecondaryAction>
                <Tooltip title="Editar">
                <IconButton
                edge="end"
                aria-label="edit"
                disabled={!auth.hasPerm("dns_blacklist.update")}
                onClick={(e) => {
                      e.stopPropagation();
                      handleEditCategory(category);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      disabled={!auth.hasPerm("dns_blacklist.delete")}
                      onClick={(e) => {
                      e.stopPropagation();
                      Swal.fire({
                        title: 'Tem certeza?',
                        text: `Deseja excluir a categoria "${category.name}"? Os domínios serão removidos.`,
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: 'Sim',
                        cancelButtonText: 'Não'
                      }).then((result) => {
                        if (result.isConfirmed) {
                        handleDeleteCategory(category);
                        }
                      });
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                  </ListItem>
                  {index < categories.length - 1 && <Divider component="li" />}
                </React.Fragment>
                )})}
              </List>
              
              {/* Controles de paginação */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="page-size-label">Itens por página</InputLabel>
                <Select
                  labelId="page-size-label"
                  id="page-size"
                  value={pageSize}
                  label="Itens por página"
                  onChange={(e) => {
                    setPageSize(e.target.value);
                    setPage(1); // Resetar para a primeira página
                  }}
                >
                  {pageSizeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body1" color="text.secondary">
              Nenhuma categoria encontrada.
            </Typography>
          </Box>
        )}
      </Paper>
    </ThemeProvider>
  );
}